/**
 * CopyableText — Reusable component that displays text with a copy icon.
 * Shows the value as inline text with a copy button next to it.
 * If value is empty or '-', renders just a dash with no copy icon.
 *
 * Pass `href` for an id that has its own detail page. The value becomes a link
 * that opens that page in a new tab, led by a copy icon button so a navigable
 * id stays copyable. The link carries the selected region, because a new tab
 * restores the region from the URL and would otherwise fall back to the
 * default one.
 *
 * Usage:
 *   <CopyableText value="37ee8a19ccc9dfe4d8cede261dcd2d57adb44ca1" />
 *   <CopyableText value={artifactId} href={`/artifact?id=${encodeURIComponent(artifactId)}`} />
 */
import Box from '@cloudscape-design/components/box';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useAppSelector } from 'src/store/hooks';
import { withRegion } from 'src/constants';

interface CopyableTextProps {
  value: string;
  /** Detail page for this value; omit for plain copyable text. */
  href?: string;
}

export const CopyableText = ({ value, href }: CopyableTextProps) => {
  const region = useAppSelector(state => state.env.region);
  if (!value || value === '-') return <Box fontSize="body-s">-</Box>;

  if (!href) {
    return (
      <CopyToClipboard
        variant="inline"
        textToCopy={value}
        copyButtonAriaLabel="Copy"
        copySuccessText="Copied"
        copyErrorText="Failed"
        copyButtonText={value}
      />
    );
  }

  const target = withRegion(href, region);

  return (
    <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
      <CopyToClipboard
        variant="icon"
        textToCopy={value}
        copyButtonAriaLabel={`Copy ${value}`}
        copySuccessText="Copied"
        copyErrorText="Failed"
      />
      {/* external renders the new-tab icon; Cloudscape adds rel="noopener noreferrer". */}
      <Link href={target} target="_blank" external externalIconAriaLabel="Opens in a new tab">
        {value}
      </Link>
    </SpaceBetween>
  );
};
